import { useTheme } from '@emotion/react';
import cn from 'classnames';
import { noop } from 'lodash';
import { type MemoExoticComponent, memo, type ReactNode, useContext } from 'react';
import type { To } from 'react-router-dom';

import { LinearProgress } from '../../../data-display/linear-progress/LinearProgress';
import { Typography } from '../../../general/typography/Typography';
import { Link } from '../../link/Link';
import { TableOfContentsSectionContext } from '../section/TableOfContentsSection.context';
import { SUB_SECTION_PROGRESS_STATE } from './constants';
import * as styles from './TableOfContentsSubSection.styles';

export type TableOfContentsSubSectionProps = {
  /** Link href. */
  href?: To;
  /** Children (table of content sub section items) */
  children?: ReactNode;
  /** Sub section label. */
  label?: ReactNode;
  /** Text display below label. */
  subLabel?: ReactNode;
  /** Value of progress bar. */
  progress?: number;
  /** State of the progress. */
  progressState?: SUB_SECTION_PROGRESS_STATE;
  /** Is the TableOfContents sub section item currently selected. */
  isSelected?: boolean;
  /** Callback for when the item is clicked. */
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
};

export const TableOfContentsSubSectionBase = ({
  children = null,
  label = null,
  href = null,
  subLabel = null,
  progress = null,
  progressState = SUB_SECTION_PROGRESS_STATE.NONE,
  isSelected = false,
  onClick = noop,
}: TableOfContentsSubSectionProps) => {
  const theme = useTheme();

  const { variant } = useContext(TableOfContentsSectionContext);

  return (
    <li css={styles.container}>
      <Link
        to={href}
        className={cn(variant, { isSelected })}
        css={styles.link}
        isDisabled={!href}
        onClick={onClick}
      >
        <div css={styles.label}>
          <Typography variant={Typography.VARIANTS.BUTTON_MEDIUM_REGULAR}>{label}</Typography>

          {!!subLabel && (
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR}>{subLabel}</Typography>
          )}

          {progressState !== SUB_SECTION_PROGRESS_STATE.NONE && !subLabel && (
            <LinearProgress
              css={styles.progress}
              progress={progress}
              backgroundColor={
                progressState === SUB_SECTION_PROGRESS_STATE.IN_PROGRESS
                  ? theme.colors.primary[25]
                  : theme.colors.danger[400]
              }
              progressColor={
                progressState === SUB_SECTION_PROGRESS_STATE.IN_PROGRESS
                  ? theme.colors.primary[400]
                  : theme.colors.success[500]
              }
            />
          )}
        </div>
      </Link>

      <ul>{children}</ul>
    </li>
  );
};

TableOfContentsSubSectionBase.displayName = 'TableOfContentsSubSection';

export const TableOfContentsSubSection: MemoExoticComponent<
  typeof TableOfContentsSubSectionBase
> & {
  PROGRESS_STATE?: typeof SUB_SECTION_PROGRESS_STATE;
} = memo(TableOfContentsSubSectionBase);

TableOfContentsSubSection.PROGRESS_STATE = SUB_SECTION_PROGRESS_STATE;
